import { JSONContent } from '@tiptap/core';
import camelCase from 'lodash/camelCase';
import { v6 as uuid } from 'uuid';

import {
  DynamicArtifactTemplate,
  GroupBlock,
  TextFieldBlock,
} from '@eluve/llm-outputs';
import {
  markdownToTiptapJson,
  tiptapJsonToMarkdown,
} from '@eluve/markdown-utils';

import { TemplateGroupAttributes } from './TemplateGroupNode';
import { TemplateSectionAttributes } from './TemplateSectionNode';

/**
 * Translation layer between the TipTap section editor document and the
 * DynamicArtifactTemplate stored on output templates.
 *
 * Each templateSection node maps to a `text` block:
 * - title  -> label (key derived via camelCase, same as the legacy builder)
 * - AI on  -> body markdown stored as `description` (the sample/instructions
 *             the model fills in)
 * - AI off -> body markdown stored as `placeholderText` (verbatim static
 *             content for manual charting)
 *
 * Each templateGroup node maps to a `group` block of text blocks (one
 * level only — groups cannot nest).
 */

const EMPTY_PARAGRAPH: JSONContent = { type: 'paragraph' };

const sectionBodyToMarkdown = (content: JSONContent[] | undefined): string => {
  const converted = tiptapJsonToMarkdown({
    jsonContent: { type: 'doc', content: content ?? [] },
  });
  return converted.ok ? converted.result.trim() : '';
};

const markdownToSectionBody = (markdown: string): JSONContent[] => {
  if (!markdown.trim()) {
    return [EMPTY_PARAGRAPH];
  }
  const converted = markdownToTiptapJson({ markdown });
  if (!converted.ok || !converted.result.content?.length) {
    return [{ type: 'paragraph', content: [{ type: 'text', text: markdown }] }];
  }
  return converted.result.content;
};

export const makeSectionNode = ({
  label = '',
  isRequired = false,
  isAiEnabled = true,
  body = [EMPTY_PARAGRAPH],
}: {
  label?: string;
  isRequired?: boolean;
  isAiEnabled?: boolean;
  body?: JSONContent[];
} = {}): JSONContent => ({
  type: 'templateSection',
  attrs: {
    sectionId: uuid(),
    isRequired,
    isAiEnabled,
  } satisfies TemplateSectionAttributes,
  content: [
    {
      type: 'sectionTitle',
      content: label ? [{ type: 'text', text: label }] : [],
    },
    ...body,
  ],
});

export const makeGroupNode = ({
  label = '',
  sections = [makeSectionNode()],
}: {
  label?: string;
  sections?: JSONContent[];
} = {}): JSONContent => ({
  type: 'templateGroup',
  attrs: { groupId: uuid() } satisfies TemplateGroupAttributes,
  content: [
    {
      type: 'groupTitle',
      content: label ? [{ type: 'text', text: label }] : [],
    },
    ...sections,
  ],
});

const getTitleText = (
  node: JSONContent,
  titleType: 'sectionTitle' | 'groupTitle',
): string => {
  const titleNode = node.content?.find((child) => child.type === titleType);
  return (titleNode?.content ?? [])
    .map((child) => child.text ?? '')
    .join('')
    .trim();
};

/**
 * Keys live in a flat namespace per nesting level (each level becomes
 * one z.object for generation), so duplicate titles must still yield
 * unique keys — otherwise the LLM fills one value into every block
 * sharing the key. Suffixes are deterministic: plan, plan2, plan3…
 */
const makeKeyDeduper = () => {
  const used = new Set<string>();
  return (base: string): string => {
    let key = base;
    let counter = 2;
    while (used.has(key)) {
      key = `${base}${counter}`;
      counter += 1;
    }
    used.add(key);
    return key;
  };
};

const sectionToTextBlock = (
  section: JSONContent,
  index: number,
  dedupeKey: (base: string) => string,
): TextFieldBlock => {
  const attrs = (section.attrs ?? {}) as Partial<TemplateSectionAttributes>;
  const label = getTitleText(section, 'sectionTitle') || `Section ${index + 1}`;
  const isAiEnabled = attrs.isAiEnabled ?? true;
  const bodyMarkdown = sectionBodyToMarkdown(
    section.content?.filter((node) => node.type !== 'sectionTitle'),
  );

  return {
    type: 'text',
    key: dedupeKey(camelCase(label) || `section${index + 1}`),
    label,
    isRequired: attrs.isRequired ?? false,
    isAiEnabled,
    description: isAiEnabled ? bodyMarkdown : undefined,
    placeholderText: isAiEnabled ? undefined : bodyMarkdown,
  };
};

/**
 * Convert the editor document into a DynamicArtifactTemplate of text
 * blocks and (one level of) group blocks.
 */
export const docToDynamicArtifact = ({
  doc,
  name,
}: {
  doc: JSONContent;
  name: string;
}): DynamicArtifactTemplate => {
  const dedupeTopLevelKey = makeKeyDeduper();
  const blocks = (doc.content ?? []).flatMap<TextFieldBlock | GroupBlock>(
    (node, index) => {
      if (node.type === 'templateSection') {
        return [sectionToTextBlock(node, index, dedupeTopLevelKey)];
      }
      if (node.type === 'templateGroup') {
        const label = getTitleText(node, 'groupTitle') || `Group ${index + 1}`;
        const sections = (node.content ?? []).filter(
          (child) => child.type === 'templateSection',
        );
        // Group children are their own nested object, so they get their
        // own key namespace
        const dedupeChildKey = makeKeyDeduper();
        return [
          {
            type: 'group',
            key: dedupeTopLevelKey(camelCase(label) || `group${index + 1}`),
            label,
            blocks: sections.map((section, sectionIndex) =>
              sectionToTextBlock(section, sectionIndex, dedupeChildKey),
            ),
          },
        ];
      }
      return [];
    },
  );

  return {
    name,
    blocks,
  };
};

/**
 * The generator sometimes restates the section title as the first bold
 * label of the body (e.g. a "Plan" section whose body opens with
 * "**Plan.**"), which then renders twice. Strip a leading bold label
 * when it matches the section title — but only an exact match, so genuine
 * lead-ins like "**Chief complaint & interval history.**" are preserved.
 */
const stripRepeatedTitle = (markdown: string, label: string): string => {
  const normalizedLabel = label.trim().toLowerCase();
  if (!normalizedLabel) {
    return markdown;
  }
  const leadingBold = markdown
    .trimStart()
    .match(/^\*\*\s*([^*]+?)\s*\*\*[.:]?\s*/);
  if (!leadingBold?.[1]) {
    return markdown;
  }
  const boldText = leadingBold[1]
    .replace(/[.:]+\s*$/, '')
    .trim()
    .toLowerCase();
  if (boldText !== normalizedLabel) {
    return markdown;
  }
  return markdown.trimStart().slice(leadingBold[0].length);
};

const textBlockToSection = (block: TextFieldBlock): JSONContent => {
  const isAiEnabled = block.isAiEnabled ?? true;
  const bodyMarkdown =
    (isAiEnabled
      ? (block.description ?? block.importedDescription)
      : block.placeholderText) ?? '';

  return makeSectionNode({
    label: block.label,
    isRequired: block.isRequired ?? false,
    isAiEnabled,
    body: markdownToSectionBody(stripRepeatedTitle(bodyMarkdown, block.label)),
  });
};

/**
 * Convert a DynamicArtifactTemplate into an editor document. Top-level
 * group blocks become editor groups; nested groups are flattened into
 * their parent (only one level is supported); non-text leaf blocks are
 * skipped.
 */
export const dynamicArtifactToDoc = (
  template: DynamicArtifactTemplate,
): JSONContent => {
  const flattenTextBlocks = (
    blocks: DynamicArtifactTemplate['blocks'],
  ): TextFieldBlock[] =>
    blocks.flatMap((block) => {
      if (block.type === 'group') {
        return flattenTextBlocks(block.blocks);
      }
      return block.type === 'text' ? [block] : [];
    });

  const nodes = template.blocks.flatMap<JSONContent>((block) => {
    if (block.type === 'group') {
      const sections = flattenTextBlocks(block.blocks).map(textBlockToSection);
      if (!sections.length) {
        return [];
      }
      return [makeGroupNode({ label: block.label, sections })];
    }
    return block.type === 'text' ? [textBlockToSection(block)] : [];
  });

  return {
    type: 'doc',
    content: nodes.length ? nodes : [makeSectionNode()],
  };
};

/** Starter document for a brand-new template (mock SOAP scaffold). */
export const makeDefaultSectionsDoc = (): JSONContent => ({
  type: 'doc',
  content: [
    makeSectionNode({
      label: 'Chief Complaint',
      isRequired: true,
      body: markdownToSectionBody(
        '[primary concern] and [additional concerns if any].',
      ),
    }),
    makeSectionNode({
      label: 'History of Present Illness',
      isRequired: true,
      body: markdownToSectionBody(
        'Patient is a [age]-year-old [sex] with [relevant history].\n\n' +
          '- [symptom description]\n' +
          '- [timing and duration]\n' +
          '- [severity and impact]\n' +
          '- [associated symptoms]',
      ),
    }),
    makeSectionNode({
      label: 'Physical Examination',
      body: markdownToSectionBody(
        'Vital signs: [vital signs]\n\nGeneral: [general appearance]',
      ),
    }),
    makeSectionNode({
      label: 'Assessment',
      isRequired: true,
      body: markdownToSectionBody(
        '1. [primary diagnosis/problem] - [status]\n' +
          '2. [secondary problems] - [status]',
      ),
    }),
    makeSectionNode({
      label: 'Plan',
      isRequired: true,
      body: markdownToSectionBody(
        '- Medications: [prescriptions and changes]\n' +
          '- Diagnostics: [tests or studies ordered]\n' +
          '- Referrals: [specialist consultations if needed]\n' +
          '- Follow-up: [next appointment and timing]',
      ),
    }),
  ],
});
